import {FormFieldBase} from '@hconnect/common/types'
import {DateRangePicker} from '@hconnect/uikit/src/lib2/components/momentDatePicker'
import {parseTimeRangeParamToMomentRange} from '@hconnect/uikit/src/lib2/components/momentDatePicker/dateRangePickerHelpers'
import moment, {Moment} from 'moment-timezone'
import {Control, FieldValues, useController, Path} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'
import {useFormInputLabelWithSuffix} from '../../../hooks'

type MomentDatePickerFormFieldProps<TField extends FieldValues> = Omit<FormFieldBase, 'name'> & {
  control: Control<TField>
  name: Path<TField>
  onChange?: (date: Moment | null) => void
  dataTestId?: string
  disableDateSwitch?: boolean
  timezone: string
}

export const MomentDatePickerFormField = <TField extends FieldValues>({
  label,
  name,
  control,
  disabled,
  readOnly,
  optional,
  onChange,
  timezone,
  dataTestId
}: MomentDatePickerFormFieldProps<TField>) => {
  const fieldDisabled = disabled || readOnly
  const dateController = useController({name, control, disabled: fieldDisabled})
  const inputLabel = useFormInputLabelWithSuffix({label, optional})
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const dateValue = dateController.field.value
  const date = dateValue ? moment(dateValue).tz(timezone) : null

  return (
    <DateRangePicker
      dataTestId={dataTestId}
      label={inputLabel}
      errorText={dateController.fieldState.error?.message}
      disabled={fieldDisabled}
      onTimeRangeParamChange={(value) => {
        const [, newEndDate] = parseTimeRangeParamToMomentRange(value, timezone)

        dateController.field.onChange(newEndDate)

        if (onChange) {
          onChange(newEndDate)
        }
      }}
      timeRangeParam={[date, date]}
      useEndOfDayForSingleDate={false}
      singleDateSwitchLabel={t('quickSelection.pickSingleDate')}
      timezone={timezone}
      noQuickSelection={true}
      fullWidth={true}
      isSingleDateDefault={true}
      disableDateSwitch={true}
      isContrastMode={false}
    />
  )
}
